import { stablePlayerHash } from './appearance';
import { isMascotId } from './catalog';
import {
  NAMED_MASCOT_IDS,
  NEW_PLAYER_MASCOT_ORDER,
  namedMascotForPlayerName,
  MascotId,
  PlayerAppearance,
} from './types';

export const PLAYER_IDENTITY_STORAGE_KEY = 'skull-arena:player-identities:v1';

const PLAYER_IDENTITY_VERSION = 1;
const COLOR_FAMILY_COUNT = 10;

const STORAGE_KEYS = {
  skullDirectory: 'skullking_player_directory_v1',
  skullCurrent: 'skullking_state_v3',
  skullPrevious: 'skullking_state_v2',
  skullLegacy: 'skullking_state',
  skullLastPlayers: 'skullking_last_players_v1',
  skullHistory: 'skullking_game_history_v1',
  flipProfiles: 'skull-arena:flip7:profiles:v1',
  flipActive: 'skull-arena:flip7:active:v1',
  flipHistory: 'skull-arena:flip7:history:v1',
  skyjoProfiles: 'skull-arena:skyjo:profiles:v1',
  skyjoActive: 'skull-arena:skyjo:active:v1',
  skyjoHistory: 'skull-arena:skyjo:history:v1',
} as const;

/**
 * The family is game-agnostic. Each game then selects the closest colour in
 * its own art direction instead of sharing a raw hex value.
 */
const SKULL_COLOR_BY_FAMILY = [0, 1, 2, 3, 4, 5, 6, 7, 4, 1] as const;
const FLIP7_COLOR_BY_FAMILY = [3, 2, 11, 10, 5, 0, 7, 6, 1, 9] as const;
const FLIP7_FAMILY_BY_COLOR = [5, 8, 1, 0, 0, 4, 7, 6, 5, 9, 3, 2, 9, 5, 6, 7, 4, 0] as const;
const SKYJO_COLOR_BY_FAMILY = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const SKYJO_FAMILY_BY_COLOR = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export type PlayerIdentityGame = 'skullking' | 'flip7' | 'skyjo';

export interface PlayerIdentity {
  key: string;
  name: string;
  mascotId: MascotId;
  colorFamily: number;
}

export interface PlayerIdentityState {
  version: 1;
  identities: PlayerIdentity[];
}

interface IdentityCandidate {
  name: string;
  mascotId?: unknown;
  colorIndex?: unknown;
  colorFamily?: unknown;
  game: PlayerIdentityGame | 'identity';
}

interface StorageValue {
  exists: boolean;
  value: unknown;
}

const emptyIdentityState = (): PlayerIdentityState => ({
  version: PLAYER_IDENTITY_VERSION,
  identities: [],
});

export function normalizePlayerIdentityName(value: string): string {
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('fr');
  return normalized === 'bouguasse' ? 'bougasse' : normalized;
}

export function canonicalPlayerDisplayName(value: string): string {
  const cleaned = value.trim().replace(/\s+/g, ' ');
  return isLegacyBouguasseName(cleaned) ? 'Bougasse' : cleaned;
}

export function playerIdentityAppearance(
  identity: PlayerIdentity,
  game: PlayerIdentityGame,
): PlayerAppearance {
  const family = normalizeColorFamily(identity.colorFamily);
  return {
    mascotId: identity.mascotId,
    colorIndex: game === 'skullking'
      ? SKULL_COLOR_BY_FAMILY[family]
      : game === 'flip7'
        ? FLIP7_COLOR_BY_FAMILY[family]
        : SKYJO_COLOR_BY_FAMILY[family],
  };
}

export function readPlayerIdentityState(): PlayerIdentityState {
  const stored = readStorage(PLAYER_IDENTITY_STORAGE_KEY);
  const candidates = identityCandidates(stored.value);
  return buildIdentityState(candidates);
}

/**
 * Returns one stable visual identity for a name. Occupied appearances are
 * considered during allocation so players never share a mascot until the
 * assignable catalogue is exhausted, even before their list is persisted.
 */
export function ensurePlayerIdentityAppearance(
  name: string,
  game: PlayerIdentityGame,
  preferred?: PlayerAppearance,
  occupied: readonly PlayerAppearance[] = [],
): PlayerAppearance {
  const key = normalizePlayerIdentityName(name);
  if (!key) {
    return {
      mascotId: preferred && isMascotId(preferred.mascotId)
        ? preferred.mascotId
        : NEW_PLAYER_MASCOT_ORDER[0],
      colorIndex: preferred && Number.isFinite(preferred.colorIndex)
        ? Math.abs(Math.trunc(preferred.colorIndex))
        : 0,
    };
  }

  const state = readPlayerIdentityState();
  const existing = state.identities.find((identity) => identity.key === key);
  if (existing) return playerIdentityAppearance(existing, game);

  const registeredMascots = new Set<MascotId>(state.identities.map((identity) => identity.mascotId));
  const registeredFamilies = new Set(state.identities.map((identity) => identity.colorFamily));
  const occupiedMascots = new Set<MascotId>();
  const occupiedFamilies = new Set<number>();
  occupied.forEach((appearance) => {
    if (isMascotId(appearance.mascotId)) occupiedMascots.add(appearance.mascotId);
    occupiedFamilies.add(colorFamilyForGame(game, appearance.colorIndex));
  });
  const registeredGeneralMascots = generalMascotSet(registeredMascots);
  const occupiedGeneralMascots = generalMascotSet(occupiedMascots);
  const usedMascots = registeredGeneralMascots.size >= NEW_PLAYER_MASCOT_ORDER.length
    ? occupiedGeneralMascots
    : new Set(Array.from(registeredGeneralMascots).concat(Array.from(occupiedGeneralMascots)));
  const usedFamilies = registeredFamilies.size >= COLOR_FAMILY_COUNT
    ? occupiedFamilies
    : new Set(Array.from(registeredFamilies).concat(Array.from(occupiedFamilies)));

  const identity = allocateIdentity(
    {
      name: cleanDisplayName(name),
      game,
      mascotId: preferred?.mascotId,
      colorIndex: preferred?.colorIndex,
    },
    usedMascots,
    usedFamilies,
  );
  const nextState: PlayerIdentityState = {
    version: PLAYER_IDENTITY_VERSION,
    identities: [...state.identities, identity],
  };
  writeStorage(PLAYER_IDENTITY_STORAGE_KEY, nextState);
  return playerIdentityAppearance(identity, game);
}

/**
 * A corrected name keeps the player's current visual identity unless it enters
 * or leaves a dedicated-name slot. Dedicated mascots never leak to other names.
 */
export function renamePlayerIdentity(
  previousName: string,
  nextName: string,
  game: PlayerIdentityGame,
  currentAppearance: PlayerAppearance,
): PlayerAppearance {
  const previousKey = normalizePlayerIdentityName(previousName);
  const nextKey = normalizePlayerIdentityName(nextName);
  if (!nextKey || previousKey === nextKey) {
    return ensurePlayerIdentityAppearance(nextName || previousName, game, currentAppearance);
  }

  const current = readPlayerIdentityState();
  const target = current.identities.find((identity) => identity.key === nextKey);
  if (target) return playerIdentityAppearance(target, game);

  const previous = current.identities.find((identity) => identity.key === previousKey);
  const remaining = current.identities.filter((identity) => identity.key !== previousKey);
  const previousFamily = previous?.colorFamily
    ?? colorFamilyForGame(game, currentAppearance.colorIndex);
  const currentMascot = previous?.mascotId ?? currentAppearance.mascotId;
  const keepCurrentMascot = isMascotId(currentMascot)
    && !(NAMED_MASCOT_IDS as readonly MascotId[]).includes(currentMascot);
  const renamed = allocateIdentity({
    name: cleanDisplayName(nextName),
    game: 'identity',
    mascotId: keepCurrentMascot ? currentMascot : undefined,
    colorFamily: previousFamily,
  }, new Set(remaining.map((identity) => identity.mascotId)), new Set());
  writeStorage(PLAYER_IDENTITY_STORAGE_KEY, {
    version: PLAYER_IDENTITY_VERSION,
    identities: [...remaining, renamed],
  } satisfies PlayerIdentityState);
  return playerIdentityAppearance(renamed, game);
}

/**
 * One idempotent startup migration reads every legacy player snapshot, builds
 * the shared identity directory, then rewrites each game with its own palette.
 */
export function synchronizeStoredPlayerIdentities(): PlayerIdentityState {
  if (!canUseStorage()) return emptyIdentityState();

  const stored = Object.fromEntries(
    Object.entries(STORAGE_KEYS).map(([name, key]) => [name, readStorage(key)]),
  ) as Record<keyof typeof STORAGE_KEYS, StorageValue>;
  const registry = readStorage(PLAYER_IDENTITY_STORAGE_KEY);
  const registryCandidates = identityCandidates(registry.value);
  const skullDirectory = skullDirectoryCandidates(stored.skullDirectory.value);
  const skullCurrent = skullStateCandidates(stored.skullCurrent.value);
  const skullPrevious = skullStateCandidates(stored.skullPrevious.value);
  const skullLegacy = skullStateCandidates(stored.skullLegacy.value);
  const skullLastPlayers = skullLastPlayerCandidates(stored.skullLastPlayers.value);
  const skullHistory = skullHistoryCandidates(stored.skullHistory.value);
  const flipProfiles = flipPlayerCandidates(stored.flipProfiles.value);
  const flipActive = flipGameCandidates(stored.flipActive.value);
  const flipHistory = flipHistoryCandidates(stored.flipHistory.value);
  const skyjoProfiles = skyjoPlayerCandidates(stored.skyjoProfiles.value);
  const skyjoActive = skyjoGameCandidates(stored.skyjoActive.value);
  const skyjoHistory = skyjoHistoryCandidates(stored.skyjoHistory.value);

  // Current tables are ordered first for uniqueness, while the preferred
  // appearance still comes from Skull King before Flip Seven for a shared name.
  const candidates = prioritizedCandidates(
    [
      ...registryCandidates,
      ...skullCurrent,
      ...skullPrevious,
      ...skullLegacy,
      ...skullLastPlayers,
      ...flipActive,
      ...flipProfiles,
      ...skyjoActive,
      ...skyjoProfiles,
      ...skullDirectory,
      ...skullHistory,
      ...flipHistory,
      ...skyjoHistory,
    ],
    [
      ...registryCandidates,
      ...skullDirectory,
      ...skullCurrent,
      ...skullPrevious,
      ...skullLegacy,
      ...skullLastPlayers,
      ...skullHistory,
      ...flipProfiles,
      ...flipActive,
      ...flipHistory,
      ...skyjoProfiles,
      ...skyjoActive,
      ...skyjoHistory,
    ],
  );
  const state = buildIdentityState(candidates);
  const identities = new Map(state.identities.map((identity) => [identity.key, identity]));

  writeStorageIfChanged(PLAYER_IDENTITY_STORAGE_KEY, registry, state);
  migrateStorageValue(STORAGE_KEYS.skullDirectory, stored.skullDirectory, (value) =>
    mapDirectory(value, identities));
  migrateStorageValue(STORAGE_KEYS.skullCurrent, stored.skullCurrent, (value) =>
    mapSkullState(value, identities));
  migrateStorageValue(STORAGE_KEYS.skullPrevious, stored.skullPrevious, (value) =>
    mapSkullState(value, identities));
  migrateStorageValue(STORAGE_KEYS.skullLegacy, stored.skullLegacy, (value) =>
    mapSkullState(value, identities));
  migrateStorageValue(STORAGE_KEYS.skullLastPlayers, stored.skullLastPlayers, (value) =>
    mapSkullLastPlayers(value, identities));
  migrateStorageValue(STORAGE_KEYS.skullHistory, stored.skullHistory, (value) =>
    mapGameList(value, identities, 'skullking'));
  migrateStorageValue(STORAGE_KEYS.flipProfiles, stored.flipProfiles, (value) =>
    mapPlayerList(value, identities, 'flip7'));
  migrateStorageValue(STORAGE_KEYS.flipActive, stored.flipActive, (value) =>
    mapGame(value, identities, 'flip7'));
  migrateStorageValue(STORAGE_KEYS.flipHistory, stored.flipHistory, (value) =>
    mapGameList(value, identities, 'flip7'));
  migrateStorageValue(STORAGE_KEYS.skyjoProfiles, stored.skyjoProfiles, (value) =>
    mapPlayerList(value, identities, 'skyjo'));
  migrateStorageValue(STORAGE_KEYS.skyjoActive, stored.skyjoActive, (value) =>
    mapGame(value, identities, 'skyjo'));
  migrateStorageValue(STORAGE_KEYS.skyjoHistory, stored.skyjoHistory, (value) =>
    mapGameList(value, identities, 'skyjo'));

  return state;
}

function buildIdentityState(candidates: IdentityCandidate[]): PlayerIdentityState {
  const identities: PlayerIdentity[] = [];
  const knownNames = new Set<string>();
  const usedMascots = new Set<MascotId>();
  const usedFamilies = new Set<number>();

  candidates.forEach((candidate) => {
    const key = normalizePlayerIdentityName(candidate.name);
    if (!key || knownNames.has(key)) return;
    const identity = allocateIdentity(candidate, usedMascots, usedFamilies);
    knownNames.add(key);
    usedMascots.add(identity.mascotId);
    usedFamilies.add(identity.colorFamily);
    identities.push(identity);
  });

  return { version: PLAYER_IDENTITY_VERSION, identities };
}

function prioritizedCandidates(
  ordered: IdentityCandidate[],
  preferred: IdentityCandidate[],
): IdentityCandidate[] {
  const preferredByName = new Map<string, IdentityCandidate>();
  preferred.forEach((candidate) => {
    const key = normalizePlayerIdentityName(candidate.name);
    if (key && !preferredByName.has(key)) preferredByName.set(key, candidate);
  });
  const emitted = new Set<string>();
  const result: IdentityCandidate[] = [];
  [...ordered, ...preferred].forEach((candidate) => {
    const key = normalizePlayerIdentityName(candidate.name);
    if (!key || emitted.has(key)) return;
    emitted.add(key);
    result.push(preferredByName.get(key) ?? candidate);
  });
  return result;
}

function allocateIdentity(
  candidate: IdentityCandidate,
  usedMascots: ReadonlySet<MascotId>,
  usedFamilies: ReadonlySet<number>,
): PlayerIdentity {
  const key = normalizePlayerIdentityName(candidate.name);
  const namedMascot = namedMascotForPlayerName(candidate.name);
  const preferredMascot = isMascotId(candidate.mascotId) ? candidate.mascotId : undefined;
  const usedGeneralMascots = generalMascotSet(usedMascots);
  const preferredGeneralMascot = preferredMascot
    && (NEW_PLAYER_MASCOT_ORDER as readonly MascotId[]).includes(preferredMascot)
    ? preferredMascot
    : undefined;
  const mascotId = namedMascot
    ?? (preferredGeneralMascot
      && (!usedGeneralMascots.has(preferredGeneralMascot)
        || usedGeneralMascots.size >= NEW_PLAYER_MASCOT_ORDER.length)
      ? preferredGeneralMascot
      : NEW_PLAYER_MASCOT_ORDER.find((mascot) => !usedGeneralMascots.has(mascot))
        ?? NEW_PLAYER_MASCOT_ORDER[stablePlayerHash(key) % NEW_PLAYER_MASCOT_ORDER.length]);
  const preferredFamily = candidate.game === 'identity'
    ? normalizeColorFamily(candidate.colorFamily)
    : colorFamilyForGame(candidate.game, candidate.colorIndex);
  const colorFamily = !usedFamilies.has(preferredFamily) || usedFamilies.size >= COLOR_FAMILY_COUNT
    ? preferredFamily
    : firstUnusedColorFamily(usedFamilies) ?? stablePlayerHash(key) % COLOR_FAMILY_COUNT;

  return {
    key,
    name: cleanDisplayName(candidate.name),
    mascotId,
    colorFamily,
  };
}

function generalMascotSet(values: Iterable<MascotId>): Set<MascotId> {
  return new Set(Array.from(values).filter((mascotId) =>
    (NEW_PLAYER_MASCOT_ORDER as readonly MascotId[]).includes(mascotId)));
}

function identityCandidates(value: unknown): IdentityCandidate[] {
  if (!isRecord(value) || !Array.isArray(value.identities)) return [];
  return value.identities.flatMap((identity) => {
    if (!isRecord(identity) || typeof identity.name !== 'string') return [];
    return [{
      name: identity.name,
      mascotId: identity.mascotId,
      colorFamily: identity.colorFamily,
      game: 'identity' as const,
    }];
  });
}

function skullDirectoryCandidates(value: unknown): IdentityCandidate[] {
  return isRecord(value) && Array.isArray(value.players)
    ? skullPlayerCandidates(value.players)
    : [];
}

function skullStateCandidates(value: unknown): IdentityCandidate[] {
  if (!isRecord(value)) return [];
  const game = isRecord(value.game) ? value.game : value;
  return Array.isArray(game.players) ? skullPlayerCandidates(game.players) : [];
}

function skullLastPlayerCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((player, index): IdentityCandidate[] => {
    if (typeof player === 'string') return [{ name: player, colorIndex: index, game: 'skullking' as const }];
    if (!isRecord(player) || typeof player.name !== 'string') return [];
    const appearance = isRecord(player.appearance) ? player.appearance : {};
    return [{
      name: player.name,
      mascotId: appearance.mascotId,
      colorIndex: appearance.colorIndex ?? index,
      game: 'skullking' as const,
    }];
  });
}

function skullHistoryCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((game): IdentityCandidate[] => isRecord(game) && Array.isArray(game.players)
    ? skullPlayerCandidates(game.players)
    : []);
}

function skullPlayerCandidates(value: unknown[]): IdentityCandidate[] {
  return value.flatMap((player, index): IdentityCandidate[] => {
    if (!isRecord(player) || typeof player.name !== 'string') return [];
    const appearance = isRecord(player.appearance) ? player.appearance : {};
    return [{
      name: player.name,
      mascotId: appearance.mascotId,
      colorIndex: appearance.colorIndex ?? index,
      game: 'skullking' as const,
    }];
  });
}

function flipPlayerCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((player, index): IdentityCandidate[] => {
    if (!isRecord(player) || typeof player.name !== 'string') return [];
    return [{
      name: player.name,
      mascotId: player.mascotId,
      colorIndex: player.colorIndex ?? index,
      game: 'flip7' as const,
    }];
  });
}

function flipGameCandidates(value: unknown): IdentityCandidate[] {
  return isRecord(value) && Array.isArray(value.players)
    ? flipPlayerCandidates(value.players)
    : [];
}

function flipHistoryCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((game): IdentityCandidate[] => flipGameCandidates(game));
}

function skyjoPlayerCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((player, index): IdentityCandidate[] => {
    if (!isRecord(player) || typeof player.name !== 'string') return [];
    return [{
      name: player.name,
      mascotId: player.mascotId,
      colorIndex: player.colorIndex ?? index,
      game: 'skyjo' as const,
    }];
  });
}

function skyjoGameCandidates(value: unknown): IdentityCandidate[] {
  return isRecord(value) && Array.isArray(value.players)
    ? skyjoPlayerCandidates(value.players)
    : [];
}

function skyjoHistoryCandidates(value: unknown): IdentityCandidate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((game): IdentityCandidate[] => skyjoGameCandidates(game));
}

function mapDirectory(value: unknown, identities: ReadonlyMap<string, PlayerIdentity>): unknown {
  if (!isRecord(value) || !Array.isArray(value.players)) return value;
  return { ...value, players: mapPlayerList(value.players, identities, 'skullking') };
}

function mapSkullState(value: unknown, identities: ReadonlyMap<string, PlayerIdentity>): unknown {
  if (!isRecord(value)) return value;
  if (isRecord(value.game) && Array.isArray(value.game.players)) {
    return { ...value, game: mapGame(value.game, identities, 'skullking') };
  }
  return mapGame(value, identities, 'skullking');
}

function mapSkullLastPlayers(value: unknown, identities: ReadonlyMap<string, PlayerIdentity>): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((player) => {
    const record = typeof player === 'string' ? { name: player } : player;
    if (!isRecord(record) || typeof record.name !== 'string') return player;
    const identity = identities.get(normalizePlayerIdentityName(record.name));
    return identity
      ? { ...record, name: correctedLegacyDisplayName(record.name, identity.name), appearance: playerIdentityAppearance(identity, 'skullking') }
      : player;
  });
}

function mapGameList(
  value: unknown,
  identities: ReadonlyMap<string, PlayerIdentity>,
  game: PlayerIdentityGame,
): unknown {
  return Array.isArray(value) ? value.map((entry) => mapGame(entry, identities, game)) : value;
}

function mapGame(
  value: unknown,
  identities: ReadonlyMap<string, PlayerIdentity>,
  game: PlayerIdentityGame,
): unknown {
  if (!isRecord(value) || !Array.isArray(value.players)) return value;
  return { ...value, players: mapPlayerList(value.players, identities, game) };
}

function mapPlayerList(
  value: unknown,
  identities: ReadonlyMap<string, PlayerIdentity>,
  game: PlayerIdentityGame,
): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((player) => {
    if (!isRecord(player) || typeof player.name !== 'string') return player;
    const identity = identities.get(normalizePlayerIdentityName(player.name));
    if (!identity) return player;
    const appearance = playerIdentityAppearance(identity, game);
    return game === 'skullking'
      ? { ...player, name: correctedLegacyDisplayName(player.name, identity.name), appearance }
      : { ...player, name: correctedLegacyDisplayName(player.name, identity.name), mascotId: appearance.mascotId, colorIndex: appearance.colorIndex };
  });
}

function colorFamilyForGame(game: PlayerIdentityGame, value: unknown): number {
  const index = typeof value === 'number' && Number.isFinite(value)
    ? Math.abs(Math.trunc(value))
    : 0;
  if (game === 'skullking') return index < 8 ? index : index % 8;
  if (game === 'flip7') return FLIP7_FAMILY_BY_COLOR[index % FLIP7_FAMILY_BY_COLOR.length];
  return SKYJO_FAMILY_BY_COLOR[index % SKYJO_FAMILY_BY_COLOR.length];
}

function normalizeColorFamily(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.abs(Math.trunc(value)) % COLOR_FAMILY_COUNT
    : 0;
}

function firstUnusedColorFamily(used: ReadonlySet<number>): number | undefined {
  for (let family = 0; family < COLOR_FAMILY_COUNT; family += 1) {
    if (!used.has(family)) return family;
  }
  return undefined;
}

function cleanDisplayName(value: string): string {
  return canonicalPlayerDisplayName(value);
}

function correctedLegacyDisplayName(value: string, canonical: string): string {
  return isLegacyBouguasseName(value) ? canonical : value;
}

function isLegacyBouguasseName(value: string): boolean {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('fr') === 'bouguasse';
}

function migrateStorageValue(
  key: string,
  stored: StorageValue,
  migrate: (value: unknown) => unknown,
): void {
  if (!stored.exists) return;
  writeStorageIfChanged(key, stored, migrate(stored.value));
}

function readStorage(key: string): StorageValue {
  if (!canUseStorage()) return { exists: false, value: null };
  try {
    const raw = window.localStorage.getItem(key);
    return { exists: raw !== null, value: raw === null ? null : JSON.parse(raw) };
  } catch {
    return { exists: false, value: null };
  }
}

function writeStorageIfChanged(key: string, previous: StorageValue, value: unknown): void {
  if (!previous.exists || JSON.stringify(previous.value) !== JSON.stringify(value)) {
    writeStorage(key, value);
  }
}

function writeStorage(key: string, value: unknown): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The games keep their in-memory state when browser storage is unavailable.
  }
}

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
